import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
} from '@mui/material';
import NumberRangeQuestion from '../../questions/numberRange/NumberRangeQuestion';
import {
  DigitalQuestion,
  DigitalSubmission,
  DigitalSubmissionChoiceWithText,
  DigitalSubmissionMultiChoice,
  DigitalSubmissionNumberRange,
  DigitalSubmissionRatio,
  DigitalSubmissionSingleChoice,
  DigitalSubmissionString,
  PartialBy,
  QuestionTypeV2,
} from '../../../redux/types';
import MultiChoiceQuestion from '../../questions/multiChoice/MultiChoiceQuestion';
import SingleChoiceQuestion from '../../questions/singleChoice/SingleChoiceQuestion';
import { DigitalSubmissionRequest } from '../../../loopback/apiClient/DRTApi';
import ApiClient from '../../../loopback/apiClient';
import StringQuestion from '../../questions/string/StringQuestion';
import RatioQuestion from '../../questions/ratio/RatioQuestion';
import ChoiceWithTextQuestion from '../../questions/choiceWithText/ChoiceWithTextQuestion';
import SingleChoiceWithTextQuestion from '../../questions/singleChoiceWithText/SingleChoiceWithTextQuestion';
import MultiChoiceWithTextQuestion from '../../questions/multiChoiceWithText/MultiChoiceWithTextQuestion';
import { CompulsoryCtxSettingMapType } from '../../../hooks/survey/usePendingCompulsoryCtxQuestions';
import { QuestionSaveOp } from '../../../hooks/survey/useQuestionSaveStatus';

type Props = {
  trialId: number;
  sectionId: number;
  enrollmentId: number;
  questions: DigitalQuestion[];
  contextSubmissionByQuestionId: Record<number, DigitalSubmission | undefined>;
  disabled?: boolean;
  summarizedView?: boolean;
  addOrUpdateDigitalSubmission?: (submission: DigitalSubmission) => void;
  // navigateToQuestion enables hover effects and click on context question container divs
  navigateToQuestion?: (qId: number) => void;
  hilightedQuestionId?: number;
  setHilightedQuestion?: React.Dispatch<React.SetStateAction<number | undefined>>,
  compulsoryCtxSettingMap?: CompulsoryCtxSettingMapType;
  showErrorNotification?: (msg: string) => void;
  getSaveStatus?: (op: QuestionSaveOp, qId: number, caseId?: number) => void;
  saveOnBlur?: boolean;
};

const ContextQuestionInputForm: React.FC<Props> = ({
  trialId,
  sectionId,
  enrollmentId,
  questions,
  contextSubmissionByQuestionId,
  disabled,
  summarizedView,
  addOrUpdateDigitalSubmission,
  navigateToQuestion,
  hilightedQuestionId: hqId,
  setHilightedQuestion,
  compulsoryCtxSettingMap,
  showErrorNotification,
  getSaveStatus,
  saveOnBlur,
}: Props) => {
  const anchorEl = useRef<HTMLElement>();
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    // remove highlight effect in after timeout
    if (setHilightedQuestion && hqId !== undefined) {
      timeout = setTimeout(() => {
        setHilightedQuestion(undefined);
      }, 1250);
    }
    // scroll to question DOM
    if (anchorEl.current && hqId) {
      anchorEl.current.scrollIntoView({ behavior: 'smooth' });
      anchorEl.current = undefined;
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [setHilightedQuestion, hqId]);

  const onSaveHandler = useCallback(async (val?: PartialBy<DigitalSubmission, 'id'>) => {
    if (val === undefined || !addOrUpdateDigitalSubmission) {
      return;
    }
    const reqPayload: DigitalSubmissionRequest = {
      answer: val.answer,
      digitalQuestionId: val.digitalQuestionId,
      digitalSectionId: val.digitalSectionId,
      type: val.type,
      trialId,
    };
    try {
      const savedSubmission = await ApiClient.addOrUpdateDigitalSubmission(reqPayload);
      addOrUpdateDigitalSubmission(savedSubmission);
    } catch (e) {
      if (showErrorNotification) {
        showErrorNotification(`Error saving digital submission ${e}`);
      }
    }
  }, [addOrUpdateDigitalSubmission, showErrorNotification, trialId]);

  return (
    <Box>
      {
        questions.map((q) => (
          <Box
            key={q.id}
            ref={q.id === hqId ? anchorEl : undefined}
            sx={navigateToQuestion
              ? {
                '&:hover': {
                  backgroundColor: 'ButtonFace',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  mx: '-10px',
                  px: '10px',
                },
              } : {
                transition: 'background-color 0.5s ease-in-out',
                backgroundColor: q.id === hqId ? '#eff58c' : '',
                mx: '-10px',
                px: '10px',
              }}
            onClick={() => navigateToQuestion && navigateToQuestion(q.id)}
          >
            <Box sx={{
              p: 3,
              bgcolor: compulsoryCtxSettingMap?.[q.id]?.compulsory ? '#fdeded' : '',
            }}
            >
              {
                q.type === QuestionTypeV2.NumberRange && (
                  <NumberRangeQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      contextSubmissionByQuestionId[q.id] as DigitalSubmissionNumberRange
                    }
                    getSaveStatus={getSaveStatus}
                    disabled={disabled}
                    saveOnBlur={saveOnBlur}
                  />
                )
              }
              {
                // @deprecated
                q.type === QuestionTypeV2.SingleChoice && (
                  <SingleChoiceQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      contextSubmissionByQuestionId[q.id] as DigitalSubmissionSingleChoice
                    }
                    disabled={disabled}
                    summarizedView={summarizedView}
                  />
                )
              }
              {
                // @deprecated
                q.type === QuestionTypeV2.MultiChoice && (
                  <MultiChoiceQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      contextSubmissionByQuestionId[q.id] as DigitalSubmissionMultiChoice
                    }
                    disabled={disabled}
                    summarizedView={summarizedView}
                  />
                )
              }
              {
                q.type === QuestionTypeV2.String && (
                  <StringQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    disabled={disabled}
                    defaultSubmission={
                      contextSubmissionByQuestionId[q.id] as DigitalSubmissionString
                    }
                    getSaveStatus={getSaveStatus}
                    saveOnBlur={saveOnBlur}
                  />
                )
              }
              {
                q.type === QuestionTypeV2.Ratio && (
                  <RatioQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      contextSubmissionByQuestionId[q.id] as DigitalSubmissionRatio
                    }
                    getSaveStatus={getSaveStatus}
                    disabled={disabled}
                    saveOnBlur={saveOnBlur}
                  />
                )
              }
              {
                // @deprecated
                q.type === QuestionTypeV2.ChoiceWithText && (
                  <ChoiceWithTextQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    defaultSubmission={
                      contextSubmissionByQuestionId[q.id] as
                      DigitalSubmissionChoiceWithText<QuestionTypeV2.ChoiceWithText>
                    }
                    disabled={disabled}
                    summarizedView={summarizedView}
                  />
                )
              }
              {
                q.type === QuestionTypeV2.SingleChoiceWithText && (
                  <SingleChoiceWithTextQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    defaultSubmission={contextSubmissionByQuestionId[q.id] as
                      DigitalSubmissionChoiceWithText<QuestionTypeV2.SingleChoiceWithText>}
                    getSaveStatus={getSaveStatus}
                    disabled={disabled}
                    saveOnBlur={saveOnBlur}
                  />
                )
              }
              {
                q.type === QuestionTypeV2.MultiChoiceWithText && (
                  <MultiChoiceWithTextQuestion
                    mode="submit"
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                    question={q}
                    onSave={onSaveHandler}
                    defaultSubmission={contextSubmissionByQuestionId[q.id] as
                      DigitalSubmissionChoiceWithText<QuestionTypeV2.MultiChoiceWithText>}
                    getSaveStatus={getSaveStatus}
                    disabled={disabled}
                    saveOnBlur={saveOnBlur}
                  />
                )
              }
            </Box>
          </Box>
        ))
      }
    </Box>
  );
};

export default ContextQuestionInputForm;
